/**
 * Agent Configs Export/Import Panel
 * Export and import agent configurations
 * Matches the old admin interface
 */

import React, { useState, useRef } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Download, Upload, AlertCircle, Check, X, FileJson } from 'lucide-react';
import { agentApi } from '@/lib/api';
import { useI18n } from '@/i18n';

interface ImportResult {
  imported_count: number;
  skipped_count: number;
  error_count: number;
  details?: Array<{ agent_type: string; status: string; message?: string }>;
}

export const AgentConfigsExportImport: React.FC = () => {
  const { messages } = useI18n();
  const t = messages.systemSettings.exportImport;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importMode, setImportMode] = useState<'create-only' | 'overwrite' | 'merge'>('create-only');
  const [skipErrors, setSkipErrors] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<{ configurations: Array<{ agent_type: string; name?: string }> } | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const blob = await agentApi.export();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `all-agents-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      alert(t?.exportSuccess);
    } catch (error) {
      alert(`${t?.exportFailed}: ${(error as Error).message}`);
    } finally {
      setExportLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setSelectedFile(file);
    setPreviewData(null);
    setImportResult(null);

    // Read and validate the file
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);

        // Validate format
        if (!data.configurations || !Array.isArray(data.configurations)) {
          throw new Error(t?.invalidFormat || 'Invalid format: missing configurations array');
        }

        setPreviewData(data);
      } catch (error) {
        alert(`${t?.invalidJson}: ${(error as Error).message}`);
        setSelectedFile(null);
        setPreviewData(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!selectedFile) {
      alert(t?.noFileSelected);
      return;
    }

    setImportLoading(true);
    setImportResult(null);

    try {
      const result = await agentApi.import(selectedFile, importMode, skipErrors);
      setImportResult(result);

      // Build success message
      let message = `${t?.importCompleted}: ${result.imported_count} ${t?.imported}`;
      if (result.skipped_count > 0) {
        message += `, ${result.skipped_count} ${t?.skipped}`;
      }
      if (result.error_count > 0) {
        message += `, ${result.error_count} ${t?.errors}`;
      }
      alert(message);

      // Reset file selection on success
      if (result.error_count === 0) {
        setSelectedFile(null);
        setPreviewData(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error) {
      alert(`${t?.importFailed}: ${(error as Error).message}`);
    } finally {
      setImportLoading(false);
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setPreviewData(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Export Section */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Download className="w-5 h-5 mr-2 text-blue-500" />
          {t?.exportTitle}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {t?.exportDescription}
        </p>
        <Button onClick={handleExport} disabled={exportLoading}>
          <Download className="w-4 h-4 mr-2" />
          {exportLoading ? t?.exporting : t?.exportAll}
        </Button>
      </Card>

      {/* Import Section */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Upload className="w-5 h-5 mr-2 text-green-500" />
          {t?.importTitle}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {t?.importDescription}
        </p>

        {/* File Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t?.selectFile}
          </label>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4
                       file:rounded-lg file:border-0 file:text-sm file:font-medium
                       file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {selectedFile && (
              <button
                onClick={handleClearFile}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {selectedFile && (
            <p className="mt-1 text-sm text-gray-500">
              <FileJson className="w-4 h-4 inline mr-1" />
              {selectedFile.name}
            </p>
          )}
        </div>

        {/* Import Mode */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t?.importMode}
          </label>
          <select
            value={importMode}
            onChange={(e) => setImportMode(e.target.value as 'create-only' | 'overwrite' | 'merge')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="create-only">{t?.importModeOptions?.createOnly}</option>
            <option value="overwrite">{t?.importModeOptions?.overwrite}</option>
            <option value="merge">{t?.importModeOptions?.merge}</option>
          </select>
          <p className="mt-1 text-xs text-gray-500">
            {importMode === 'create-only' && t?.importModeHints?.createOnly}
            {importMode === 'overwrite' && t?.importModeHints?.overwrite}
            {importMode === 'merge' && t?.importModeHints?.merge}
          </p>
        </div>

        {/* Skip Errors */}
        <div className="mb-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={skipErrors}
              onChange={(e) => setSkipErrors(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-sm text-gray-700">{t?.skipErrors}</span>
          </label>
          <p className="mt-1 text-xs text-gray-500 ml-6">
            {t?.skipErrorsHint}
          </p>
        </div>

        {/* Preview */}
        {previewData && (
          <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-2">{t?.importPreview}</h4>
            <p className="text-sm text-gray-600 mb-2">
              {t?.foundConfigs?.replace('{count}', String(previewData.configurations.length))}
            </p>
            <div className="max-h-40 overflow-y-auto">
              <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                {previewData.configurations.map((config, index) => (
                  <li key={index}>
                    <strong>{config.agent_type}</strong>
                    {config.name && <span className="text-gray-400"> - {config.name}</span>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Import Result */}
        {importResult && (
          <div className={`mb-4 p-4 rounded-lg border ${
            importResult.error_count > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'
          }`}>
            <h4 className="text-sm font-medium mb-2 flex items-center">
              {importResult.error_count > 0 ? (
                <AlertCircle className="w-4 h-4 mr-2 text-yellow-500" />
              ) : (
                <Check className="w-4 h-4 mr-2 text-green-500" />
              )}
              {t?.importResults}
            </h4>
            <div className="text-sm space-y-1">
              <p className="text-green-700">{t?.imported}: {importResult.imported_count}</p>
              {importResult.skipped_count > 0 && (
                <p className="text-gray-600">{t?.skipped}: {importResult.skipped_count}</p>
              )}
              {importResult.error_count > 0 && (
                <p className="text-red-600">{t?.errors}: {importResult.error_count}</p>
              )}
            </div>
            {importResult.details && importResult.details.length > 0 && (
              <div className="mt-2 max-h-32 overflow-y-auto">
                <ul className="text-xs space-y-1">
                  {importResult.details.map((detail, index) => (
                    <li key={index} className={
                      detail.status === 'error' ? 'text-red-600' :
                      detail.status === 'skipped' ? 'text-gray-500' : 'text-green-600'
                    }>
                      {detail.agent_type}: {detail.status}
                      {detail.message && ` - ${detail.message}`}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Import Button */}
        <Button
          onClick={handleImport}
          disabled={!selectedFile || importLoading}
        >
          <Upload className="w-4 h-4 mr-2" />
          {importLoading ? t?.importing : t?.importConfigurations}
        </Button>
      </Card>

      {/* Info */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
        <p className="font-medium mb-2">{t?.aboutTitle}</p>
        <ul className="space-y-1 list-disc list-inside">
          {t?.aboutItems?.map((item, idx) => (
            <li key={idx} dangerouslySetInnerHTML={{ __html: item }} />
          ))}
        </ul>
      </div>
    </div>
  );
};
